import { Injectable, effect, inject, signal, untracked } from '@angular/core';
import { finalize, timeout } from 'rxjs';
import { LocationClient, StoreLocationInformationDto } from '../../core/services/dataservice.svc';
import { UserResolverService } from './user-resolver.service';

/**
 * Shared, cached store-location context for the app shell (navy header)
 * and any screen that needs the selected store's identity without
 * re-fetching it. Reloads automatically when the selected location changes.
 */
@Injectable({ providedIn: 'root' })
export class LocationContextService {
    private _locationClient = inject(LocationClient);
    private _userResolverService = inject(UserResolverService);

    readonly location = signal<StoreLocationInformationDto | undefined>(undefined);
    readonly loading = signal(false);

    constructor() {
        effect(onCleanup => {
            const locID = this._userResolverService.userInfo()?.LocID;

            if (!locID) {
                untracked(() => {
                    this.location.set(undefined);
                    this.loading.set(false);
                });
                return;
            }

            const subscription = untracked(() => {
                this.loading.set(true);
                return this._locationClient
                    .location_GetLocationInformation(locID)
                    .pipe(
                        timeout(30_000),
                        finalize(() => this.loading.set(false))
                    )
                    .subscribe({
                        next: location => this.location.set(location),
                        error: err => {
                            console.error('Failed to load location context', err);
                            this.location.set(undefined);
                        }
                    });
            });

            onCleanup(() => subscription.unsubscribe());
        });
    }
}
